import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import React, { Fragment } from 'react'
import { Table, TableBody, TableContainer } from '@mui/material'
import { GenericTableV2Props, TableData } from './types'
import { TableHeader } from './components/TableHeader'
import { TableRowComponent } from './components/TableRowComponent'
import { EmptyState } from './components/EmptyState'
import { LoadingState } from './components/LoadingState'
import { TablePagination } from './components/TablePagination'
import { useTableExpansion } from './hooks/useTableExpansion'

export const GenericTableV2 = <T extends TableData>({
  data,
  columns,
  keyField,
  loading = false,
  emptyMessage = 'Ajuste los criterios de búsqueda para mostrar resultados',
  noResultsComponent,
  selection,
  sorting,
  actions,
  prioritization,
  pagination,
  styling,
  expandable,
  onRowClick,
  onRowDoubleClick
}: GenericTableV2Props<T>) => {
  const styles = useStyles({
    dense: styling?.dense || false
  })

  // Hook para manejo de expansión (solo si expandable está configurado)
  const expansionHook = useTableExpansion({
    data,
    keyField,
    expandable,
    currentPage: pagination?.page
  })

  // Estados de carga y datos vacíos
  if (loading) {
    return <LoadingState />
  }

  if (!data || data.length === 0) {
    if (noResultsComponent) {
      return <Fragment>{noResultsComponent}</Fragment>
    }
    return <EmptyState message={emptyMessage} />
  }

  const hasActions = actions && actions.length > 0

  return (
    <div className={styles.tableContainer}>
      <TableContainer>
        <Table
          size={styling?.dense ? 'small' : 'medium'}
          className={styles.table}
        >
          <TableHeader
            columns={columns}
            selection={selection}
            sorting={sorting}
            prioritization={prioritization}
            expandable={expandable}
            hasActions={Boolean(hasActions)}
            data={data}
            allRowsExpanded={expansionHook.allRowsExpanded}
            hasExpandableRows={expansionHook.stats.hasExpandableRows}
            onToggleAllRows={expansionHook.toggleAllRows}
          />

          <TableBody>
            {data.map((row, index) => {
              const rowId = expansionHook.createUniqueId(row, index)
              return (
                <TableRowComponent
                  key={String(row[keyField])}
                  row={row}
                  index={index}
                  columns={columns}
                  keyField={keyField}
                  selection={selection}
                  prioritization={prioritization}
                  styling={styling}
                  expandable={expandable}
                  actions={actions}
                  onRowClick={onRowClick}
                  onRowDoubleClick={onRowDoubleClick}
                  rowId={rowId}
                  isRowExpanded={expansionHook.isRowExpanded(rowId)}
                  hasDetails={expansionHook.hasDetails(row)}
                  onToggleRowExpansion={() =>
                    expansionHook.toggleRowExpansion(rowId)
                  }
                  getDetailData={expansionHook.getDetailData}
                />
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && <TablePagination pagination={pagination} />}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  tableContainer: {
    width: '100%'
  },
  table: {
    minWidth: 650,
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  }
}))
